/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2025 Rochester Software Associates (service@rocsoft.com)
 */

import PropTypes from 'prop-types';

import useOnOffToggle from '~/utils/hooks/useOnOffToggle';
import SmartImage from '~components/images/SmartImage';

import Button from '../buttons/Button';
import PlaceholderText from '../skeleton/PlaceholderText';
import { useCurrentUser } from '../user/UserContext';

import FormattedString from './FormattedString';
import LocalizationEditModal from './LocalizationEditModal';
import { formattedStringParamtersPropType } from './rsaMessageFormat/propTypes';
import useLocalization from './useLocalization';

const LocalizedString = ({ localeKey, parameters, allowLocalizationMode = true }) => {
    const [isModalOpen, openModal, /* closeModal */, toggleModal] = useOnOffToggle(false);

    const currentUser = useCurrentUser();
    const inLocalizationMode = currentUser?.features?.localizationMode;

    const localization = useLocalization(localeKey);
    if (typeof localization !== 'string') {
        return <PlaceholderText placeholderFor={localeKey} />;
    } else {
        const formattedString = <FormattedString pattern={localization} parameters={parameters} source={localeKey} />;

        if (allowLocalizationMode && inLocalizationMode) {
            return (
                <>
                    {formattedString}
                    <span className="edit-localization-button">
                        <Button
                            type="link"
                            inline
                            action={openModal}
                        >
                            <SmartImage
                                localeKey="COMMON_UI.IMGALT.EDIT_LOCALIZATION_BUTTON"
                                name="localize"
                                tooltipPlacement="auto"
                            />
                        </Button>
                    </span>

                    {isModalOpen && (
                        <LocalizationEditModal
                            localeKey={localeKey}
                            toggleModal={toggleModal}
                        />
                    )}

                </>
            );
        } else {
            return formattedString;
        }
    }
};

LocalizedString.propTypes = {
    localeKey: PropTypes.string.isRequired,
    parameters: formattedStringParamtersPropType,
    allowLocalizationMode: PropTypes.bool,
};

export default LocalizedString;
